import React, { useContext, useState } from 'react'
import { GridContainer, GridItem } from '@entur/grid'
import { Label } from '@entur/typography'

import { FetchState, TicketRight } from '../../../api-clients/api-types'
import { getDateFromLocalDateString, getEndOfYear, tolLocalDateFormat } from '../../../utils/date-utils'
import { useEmployee } from '../../../contexts/employee-context'
import ValidatableDatePicker, { datePickerFormField } from '../../form/validatable-date-picker'
import { useUpdateManualOrder } from '../../../hooks/use-manual-order'
import { validateFromDateToDate } from '../../../utils/ticket-rights-utils'
import { useStationPairs } from '../../../hooks/use-station-pairs'
import { ChangeTicketRightModal } from '../change-ticket-right-modal'
import TicketTypeCategoryDropdown, {
  getSelectedUpdateTypeByValue,
  ticketTypeCategoryFormField,
  ticketTypeToDropdownItemType,
  UpdateTypes,
} from '../../form/ticket-type-category-dropdown'
import { CardActionChip, TicketRightCardBase } from './ticket-right-card-base'
import { useClientInfo } from '../../../contexts/client-info-context'
import { ReadOnlyContext } from '../../../contexts/read-only-context'

type StationTicketRightCardProps = {
  ticketRight: TicketRight
}
export const StationTicketRightCard: React.FC<StationTicketRightCardProps> = ({ ticketRight }) => {
  const categoryName = ticketRight.manualOrder
    ? ticketRight.manualOrder.ticketType.description.split(',')[1]?.trim()
    : 'Ikke valgt'
  const [isOpenBlueModal, setOpenBlueModal] = useState<'changeCategory' | 'selectPeriod' | 'addStations'>()
  const [validationError, setValidationError] = useState<string>()
  const { stationPairs } = useStationPairs()
  const {
    updateManualOrderResponse,
    fetchUpdatedEmployeeResponse,
    addStationPairResponse,
    updateManualOrder,
    addStationPair,
  } = useUpdateManualOrder(() => setOpenBlueModal(undefined))

  const { hasSensitiveAccess } = useClientInfo()
  const { readOnly } = useContext(ReadOnlyContext)

  const [fromDate, setFromDate] = useState(
    datePickerFormField(
      'fromDate',
      'Gyldig fra',
      ticketRight.manualOrder ? getDateFromLocalDateString(ticketRight.manualOrder.fromDate) : new Date(),
      true,
      undefined,
      true
    )
  )
  const [toDate, setToDate] = useState(
    datePickerFormField(
      'toDate',
      'Gyldig til',
      ticketRight.manualOrder ? getDateFromLocalDateString(ticketRight.manualOrder.toDate) : getEndOfYear(),
      true
    )
  )

  const [selected, setSelected] = useState(
    ticketTypeCategoryFormField(ticketTypeToDropdownItemType(ticketRight.ticketType).value)
  )
  const { employee } = useEmployee()

  const isLoading =
    updateManualOrderResponse.status === FetchState.LOADING ||
    fetchUpdatedEmployeeResponse.status === FetchState.LOADING ||
    addStationPairResponse.status === FetchState.LOADING

  let errorMessage
  if (updateManualOrderResponse.status === FetchState.ERROR) errorMessage = 'Lagring av data feilet'
  if (addStationPairResponse.status === FetchState.ERROR) errorMessage = 'Lagring av nytt stasjonspar feilet'
  if (fetchUpdatedEmployeeResponse.status === FetchState.ERROR)
    errorMessage = 'Henting av endret rettighet feilet, last siden på nytt'
  if (validationError) errorMessage = validationError

  const changeCategory = (
    <ChangeTicketRightModal
      title="Endre rettighetstype"
      onSubmit={() => {
        if (ticketRight.manualOrder?.id !== undefined) {
          const update = getSelectedUpdateTypeByValue(selected.value)
          if (update.type === UpdateTypes.TICKET_TYPE_PATH) {
            const ticketTypeId = parseInt(update.value, 10)
            if (ticketTypeId !== ticketRight.ticketType.id) {
              updateManualOrder({
                id: ticketRight.manualOrder?.id || 0,
                ticketTypeId,
              })
              setValidationError(undefined)
            } else {
              setValidationError('Ingen endring i rettighetstype')
            }
          } else {
            setValidationError('Vennligst velg en rettighetstype')
          }
        }
      }}
      isOpen={isOpenBlueModal === 'changeCategory'}
      setOpen={() => {
        setValidationError(undefined)
        setOpenBlueModal(undefined)
      }}
      isLoading={isLoading}
      errorMessage={errorMessage}
    >
      <GridContainer spacing="extraSmall">
        <GridItem small={12}>
          <TicketTypeCategoryDropdown
            formField={selected}
            company={employee.company}
            update={setSelected}
            ticketType="path"
          />
        </GridItem>
      </GridContainer>
    </ChangeTicketRightModal>
  )

  const selectPeriod = (
    <ChangeTicketRightModal
      title="Endre periode"
      onSubmit={() => {
        const dateError = validateFromDateToDate(fromDate.value, toDate.value)
        if (!dateError) {
          updateManualOrder({
            id: ticketRight.manualOrder?.id || 0,
            fromDate: tolLocalDateFormat(fromDate.value as Date),
            toDate: tolLocalDateFormat(toDate.value as Date),
          })
          setValidationError(undefined)
        } else {
          setValidationError(dateError)
        }
      }}
      isOpen={isOpenBlueModal === 'selectPeriod'}
      setOpen={() => {
        setValidationError(undefined)
        setOpenBlueModal(undefined)
      }}
      isLoading={isLoading}
      errorMessage={errorMessage}
      onClose={() => setValidationError(undefined)}
    >
      <GridContainer spacing="extraSmall">
        <GridItem small={6}>
          <ValidatableDatePicker date={fromDate} update={(date) => setFromDate(date)} />
        </GridItem>
        <GridItem small={6}>
          <ValidatableDatePicker date={toDate} update={(date) => setToDate(date)} />
        </GridItem>
      </GridContainer>
    </ChangeTicketRightModal>
  )

  const addStations = (
    <ChangeTicketRightModal
      title="Nytt stasjonspar"
      onSubmit={() => {
        const stationPair = stationPairs[0]
        if (ticketRight.manualOrder && stationPair.fromStation && stationPair.toStation) {
          addStationPair(ticketRight.manualOrder.id, stationPair.fromStation, stationPair.toStation)
          setValidationError(undefined)
        } else {
          setValidationError(`'Fra stasjon' og 'Til Stasjon' kan ikke være tomme`)
        }
      }}
      isOpen={isOpenBlueModal === 'addStations'}
      setOpen={() => {
        setValidationError(undefined)
        setOpenBlueModal(undefined)
      }}
      isLoading={isLoading}
      errorMessage={errorMessage}
      onClose={() => setValidationError(undefined)}
    >
      <GridContainer spacing="extraSmall">{stationPairs.map((pair) => pair.element)}</GridContainer>
    </ChangeTicketRightModal>
  )

  const actionChips =
    !readOnly && hasSensitiveAccess === true ? (
      <>
        <CardActionChip onClick={() => setOpenBlueModal('changeCategory')}>Endre kategori</CardActionChip>
        {isOpenBlueModal === 'changeCategory' && changeCategory}
        <CardActionChip onClick={() => setOpenBlueModal('addStations')}>Legg til stasjonspar</CardActionChip>
        {isOpenBlueModal === 'addStations' && addStations}
        <CardActionChip onClick={() => setOpenBlueModal('selectPeriod')}>Endre periode</CardActionChip>
        {isOpenBlueModal === 'selectPeriod' && selectPeriod}
      </>
    ) : (
      <></>
    )

  return (
    <TicketRightCardBase ticketRight={ticketRight} categoryName={categoryName} actionChips={actionChips}>
      <Label>Stasjoner</Label>
      <GridContainer>
        {ticketRight.manualOrder?.stations?.map((station) => (
          <GridItem key={station.id} small={12}>
            {station.fromStation.name} - {station.toStation.name}
          </GridItem>
        ))}
      </GridContainer>
    </TicketRightCardBase>
  )
}
